import { useEffect, useMemo, useState } from "react"
import {
  Bot as BotIcon,
  CheckCircle2,
  Clock3,
  Info,
  Plus,
  Send,
  Trash2,
  XCircle,
} from "lucide-react"
import { Switch } from "../components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { useLocale } from "../components/LocaleProvider"
import { toast } from "../components/ui/toast"
import {
  useBotClients,
  useBotMessages,
  useBotSettings,
  useCancelBotMessage,
  useScheduleBotMessage,
  useTestBotMessage,
  useUpdateBotClientChat,
  useUpdateBotSettings,
} from "../lib/queries"
import type {
  BotClientChat,
  BotDestination,
  BotSettings,
  ScheduleBotMessageDto,
  ScheduledMessage,
  TestBotMessageDto,
} from "../lib/api"
import type { TranslationKey } from "../i18n/translations"

type OffsetUnit = "minutes" | "hours"

interface OffsetDraft {
  id: string
  amount: number
  unit: OffsetUnit
}

let offsetId = 0

function makeDraft(amount = 1, unit: OffsetUnit = "hours"): OffsetDraft {
  offsetId += 1
  return { id: `offset-${offsetId}`, amount, unit }
}

function draftsFromMinutes(minutes: number[]): OffsetDraft[] {
  return minutes.map((minutesValue) =>
    minutesValue % 60 === 0
      ? makeDraft(minutesValue / 60, "hours")
      : makeDraft(minutesValue, "minutes"),
  )
}

function draftsToMinutes(drafts: OffsetDraft[]): number[] {
  return drafts
    .map((draft) => Math.round(draft.unit === "hours" ? draft.amount * 60 : draft.amount))
    .filter((minutes) => Number.isFinite(minutes) && minutes >= 1)
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—"
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return "—"
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`
}

function defaultSendAt(): string {
  const date = new Date(Date.now() + 60 * 60 * 1000)
  date.setSeconds(0, 0)
  return toLocalInputValue(date)
}

const STATUS_LABEL: Record<string, TranslationKey> = {
  PENDING: "bot_status_pending",
  SENT: "bot_status_sent",
  FAILED: "bot_status_failed",
  CANCELLED: "bot_status_cancelled",
}

const STATUS_CLASS: Record<string, string> = {
  PENDING: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  SENT: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  FAILED: "bg-red-500/10 text-red-600 dark:text-red-400",
  CANCELLED: "bg-muted text-muted-foreground",
}

function OffsetEditor({
  title,
  description,
  value,
  onChange,
}: {
  title: string
  description: string
  value: OffsetDraft[]
  onChange: (drafts: OffsetDraft[]) => void
}) {
  const { t } = useLocale()

  return (
    <div className="rounded-lg border bg-background/60 p-4 space-y-3">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>

      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((draft, index) => (
            <div key={draft.id} className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={draft.amount === 0 ? "" : draft.amount}
                onChange={(event) => {
                  const amount = event.target.value === "" ? 0 : Number(event.target.value)
                  onChange(
                    value.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, amount } : item,
                    ),
                  )
                }}
                className="h-9 w-24 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <select
                value={draft.unit}
                onChange={(event) => {
                  const unit = event.target.value as OffsetUnit
                  onChange(
                    value.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, unit } : item,
                    ),
                  )
                }}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="minutes">{t("bot_minutes")}</option>
                <option value="hours">{t("bot_hours")}</option>
              </select>
              <button
                type="button"
                onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
                className="h-9 w-9 rounded-md border border-input bg-background flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                aria-label={t("btn_delete")}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => onChange([...value, makeDraft()])}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      >
        <Plus className="h-3.5 w-3.5" />
        {t("bot_add_offset")}
      </button>
    </div>
  )
}

function ClientChatRow({ client }: { client: BotClientChat }) {
  const { t } = useLocale()
  const updateChat = useUpdateBotClientChat()
  const testMessage = useTestBotMessage()

  const [chatId, setChatId] = useState(client.chatId)
  const [threadId, setThreadId] = useState(client.threadId)
  const [chatEnabled, setChatEnabled] = useState(client.chatEnabled)

  useEffect(() => {
    setChatId(client.chatId)
    setThreadId(client.threadId)
    setChatEnabled(client.chatEnabled)
  }, [client.chatId, client.threadId, client.chatEnabled])

  const dirty =
    chatId !== client.chatId || threadId !== client.threadId || chatEnabled !== client.chatEnabled
  const hasSavedChat = Boolean(client.chatId) && client.chatEnabled

  const sourceKey: TranslationKey =
    client.source === "database"
      ? "bot_source_custom"
      : client.source === "default"
        ? "bot_source_default"
        : "bot_source_none"

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{client.fullName}</div>
        <div className="text-xs text-muted-foreground">
          <span className="rounded bg-muted px-1.5 py-0.5">{t(sourceKey)}</span>
          {!client.active && (
            <span className="ml-2 rounded bg-red-500/10 px-1.5 py-0.5 text-red-500">
              {t("bot_client_inactive")}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <input
          value={chatId}
          onChange={(event) => setChatId(event.target.value)}
          placeholder={t("bot_chat_placeholder")}
          className="h-9 w-48 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </TableCell>
      <TableCell>
        <input
          value={threadId}
          onChange={(event) => setThreadId(event.target.value)}
          placeholder={t("bot_thread_placeholder")}
          className="h-9 w-40 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </TableCell>
      <TableCell>
        <Switch checked={chatEnabled} onCheckedChange={setChatEnabled} />
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              const dto: TestBotMessageDto = { clientId: client.clientId }
              testMessage.mutate(dto)
            }}
            disabled={!hasSavedChat || testMessage.isPending}
            className="h-8 rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("bot_test")}
          </button>
          <button
            type="button"
            onClick={() =>
              updateChat.mutate({
                clientId: client.clientId,
                dto: {
                  chatId: chatId.trim(),
                  threadId: threadId.trim(),
                  enabled: chatEnabled,
                },
              })
            }
            disabled={!dirty || updateChat.isPending}
            className="h-8 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("btn_save")}
          </button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function Bot() {
  const { t } = useLocale()
  const settingsQuery = useBotSettings()
  const clientsQuery = useBotClients()
  const messagesQuery = useBotMessages()

  const settings = settingsQuery.data as BotSettings | undefined
  const clients = clientsQuery.data as BotClientChat[] | undefined
  const messages = messagesQuery.data as ScheduledMessage[] | undefined
  const settingsLoading = settingsQuery.isLoading
  const clientsLoading = clientsQuery.isLoading
  const messagesLoading = messagesQuery.isLoading

  const updateSettings = useUpdateBotSettings()
  const scheduleMessage = useScheduleBotMessage()
  const cancelMessage = useCancelBotMessage()
  const testMessage = useTestBotMessage()

  const [initialized, setInitialized] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [destination, setDestination] = useState<BotDestination>("client")
  const [centralChatId, setCentralChatId] = useState("")
  const [centralThreadId, setCentralThreadId] = useState("")
  const [beforeDrafts, setBeforeDrafts] = useState<OffsetDraft[]>([])
  const [afterDrafts, setAfterDrafts] = useState<OffsetDraft[]>([])

  const [clientSearch, setClientSearch] = useState("")

  const [targetMode, setTargetMode] = useState<"client" | "custom">("client")
  const [targetClientId, setTargetClientId] = useState<number | "">("")
  const [customChatId, setCustomChatId] = useState("")
  const [customThreadId, setCustomThreadId] = useState("")
  const [sendAt, setSendAt] = useState(defaultSendAt)
  const [message, setMessage] = useState("")

  useEffect(() => {
    if (!settings || initialized) return
    setEnabled(settings.enabled)
    setDestination(settings.destination)
    setCentralChatId(settings.centralChatId)
    setCentralThreadId(settings.centralThreadId)
    setBeforeDrafts(draftsFromMinutes(settings.beforeMinutes))
    setAfterDrafts(draftsFromMinutes(settings.afterMinutes))
    setInitialized(true)
  }, [settings, initialized])

  const filteredClients = useMemo(() => {
    const needle = clientSearch.trim().toLowerCase()
    const list = clients ?? []
    if (!needle) return list
    return list.filter((client) => client.fullName.toLowerCase().includes(needle))
  }, [clients, clientSearch])

  const selectedClient = useMemo(
    () => (clients ?? []).find((client) => client.clientId === targetClientId),
    [clients, targetClientId],
  )

  const handleSaveSettings = () => {
    const beforeMinutes = draftsToMinutes(beforeDrafts)
    const afterMinutes = draftsToMinutes(afterDrafts)
    if (enabled && beforeMinutes.length === 0 && afterMinutes.length === 0) {
      toast(t("bot_offsets_required"), "error")
      return
    }

    updateSettings.mutate({
      enabled,
      beforeMinutes,
      afterMinutes,
      destination,
      centralChatId,
      centralThreadId,
    })
  }

  const handleSchedule = () => {
    const text = message.trim()
    if (!text) {
      toast(t("bot_message_required"), "error")
      return
    }
    const date = new Date(sendAt)
    if (!sendAt || isNaN(date.getTime())) {
      toast(t("bot_send_at_required"), "error")
      return
    }

    const dto: ScheduleBotMessageDto = {
      message: text,
      sendAt: date.toISOString(),
    }

    if (targetMode === "client") {
      if (targetClientId === "") {
        toast(t("bot_client_placeholder"), "error")
        return
      }
      if (!selectedClient?.chatId || !selectedClient.chatEnabled) {
        toast(t("bot_no_client_chat"), "error")
        return
      }
      dto.clientId = targetClientId
    } else {
      if (!customChatId.trim()) {
        toast(t("bot_chat_required"), "error")
        return
      }
      dto.chatId = customChatId.trim()
      dto.threadId = customThreadId.trim()
    }

    scheduleMessage.mutate(dto, {
      onSuccess: () => {
        setMessage("")
        setSendAt(defaultSendAt())
      },
    })
  }

  const settingsSourceKey: TranslationKey =
    settings?.source === "database" ? "bot_source_database" : "bot_source_env"

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <BotIcon className="h-6 w-6" />
            {t("bot_title")}
          </h1>
          <p className="text-sm text-muted-foreground">{t("bot_subtitle")}</p>
        </div>
        {settings && (
          <div className="space-y-1 text-right text-xs text-muted-foreground">
            <div className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1">
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  settings.botConfigured ? "bg-emerald-500" : "bg-red-500"
                }`}
              />
              {settings.botConfigured ? t("bot_bot_ready") : t("bot_bot_missing")}
            </div>
            <div>
              {t(settingsSourceKey)} · {t("bot_timezone", settings.timeZone)}
            </div>
            {settings.updatedAt && (
              <div>{t("bot_updated", formatDateTime(settings.updatedAt))}</div>
            )}
          </div>
        )}
      </div>

      {settings && !settings.botConfigured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-400">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{t("bot_bot_missing")}</p>
        </div>
      )}

      <section className="rounded-lg border bg-card p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{t("bot_termin_reminders")}</h2>
            <p className="text-sm text-muted-foreground">{t("bot_termin_reminders_desc")}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t("bot_enabled")}</span>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </div>

        {settingsLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">{t("loading")}</div>
        ) : (
          <>
            <div className="grid gap-4 lg:grid-cols-2">
              <OffsetEditor
                title={t("bot_before")}
                description={t("bot_before_desc")}
                value={beforeDrafts}
                onChange={setBeforeDrafts}
              />
              <OffsetEditor
                title={t("bot_after")}
                description={t("bot_after_desc")}
                value={afterDrafts}
                onChange={setAfterDrafts}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("bot_destination")}</label>
                <select
                  value={destination}
                  onChange={(event) => setDestination(event.target.value as BotDestination)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="client">{t("bot_destination_client")}</option>
                  <option value="central">{t("bot_destination_central")}</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  {destination === "client"
                    ? t("bot_destination_client_desc")
                    : t("bot_destination_central_desc")}
                </p>
              </div>

              {destination === "central" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("bot_central_chat")}</label>
                    <input
                      value={centralChatId}
                      onChange={(event) => setCentralChatId(event.target.value)}
                      placeholder={t("bot_chat_placeholder")}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("bot_central_thread")}</label>
                    <input
                      value={centralThreadId}
                      onChange={(event) => setCentralThreadId(event.target.value)}
                      placeholder={t("bot_thread_placeholder")}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    {t("bot_central_hint")}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSaveSettings}
                disabled={updateSettings.isPending}
                className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("bot_save_settings")}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="rounded-lg border bg-card p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{t("bot_client_chats")}</h2>
            <p className="text-sm text-muted-foreground">{t("bot_client_chats_desc")}</p>
          </div>
          <input
            value={clientSearch}
            onChange={(event) => setClientSearch(event.target.value)}
            placeholder={t("bot_search_clients")}
            className="h-9 w-60 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <div className="max-h-[520px] overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("bot_client")}</TableHead>
                <TableHead>{t("bot_chat_id")}</TableHead>
                <TableHead>{t("bot_thread_id")}</TableHead>
                <TableHead>{t("bot_chat_enabled")}</TableHead>
                <TableHead className="text-right">{t("bot_test")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                    {t("loading")}
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                    {t("bot_no_chats")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <ClientChatRow key={client.clientId} client={client} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="rounded-lg border bg-card p-5 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">{t("bot_scheduled")}</h2>
          <p className="text-sm text-muted-foreground">{t("bot_scheduled_desc")}</p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3 rounded-lg border bg-background/60 p-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("bot_target")}</label>
              <select
                value={targetMode}
                onChange={(event) => setTargetMode(event.target.value as "client" | "custom")}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="client">{t("bot_target_client")}</option>
                <option value="custom">{t("bot_target_custom")}</option>
              </select>
            </div>

            {targetMode === "client" ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("bot_client")}</label>
                <select
                  value={targetClientId}
                  onChange={(event) =>
                    setTargetClientId(event.target.value === "" ? "" : Number(event.target.value))
                  }
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">{t("bot_client_placeholder")}</option>
                  {(clients ?? []).map((client) => (
                    <option key={client.clientId} value={client.clientId}>
                      {client.fullName}
                      {!client.chatId || !client.chatEnabled ? ` — ${t("bot_source_none")}` : ""}
                    </option>
                  ))}
                </select>
                {selectedClient && (!selectedClient.chatId || !selectedClient.chatEnabled) && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {t("bot_no_client_chat")}
                  </p>
                )}
                {selectedClient?.chatId && (
                  <p className="text-xs text-muted-foreground">
                    {selectedClient.chatId}
                    {selectedClient.threadId ? ` / ${selectedClient.threadId}` : ""}
                  </p>
                )}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("bot_custom_chat")}</label>
                  <input
                    value={customChatId}
                    onChange={(event) => setCustomChatId(event.target.value)}
                    placeholder={t("bot_chat_placeholder")}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("bot_thread_id")}</label>
                  <input
                    value={customThreadId}
                    onChange={(event) => setCustomThreadId(event.target.value)}
                    placeholder={t("bot_thread_placeholder")}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("bot_send_at")}</label>
              <input
                type="datetime-local"
                value={sendAt}
                onChange={(event) => setSendAt(event.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t("bot_message")}</label>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={4}
                placeholder={t("bot_message_placeholder")}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5" />
                {t("bot_schedule_hint")}
              </p>
              <button
                type="button"
                onClick={handleSchedule}
                disabled={scheduleMessage.isPending}
                className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {t("bot_schedule")}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-medium">{t("bot_scheduled_messages")}</h3>
              <button
                type="button"
                onClick={() => {
                  if (targetMode === "client" && selectedClient) {
                    testMessage.mutate({ clientId: selectedClient.clientId })
                  } else if (targetMode === "custom" && customChatId.trim()) {
                    testMessage.mutate({
                      chatId: customChatId.trim(),
                      threadId: customThreadId.trim(),
                    })
                  } else {
                    toast(t("bot_client_placeholder"), "error")
                  }
                }}
                disabled={testMessage.isPending}
                className="h-8 rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t("bot_test")}
              </button>
            </div>

            <div className="max-h-[420px] overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("bot_send_at")}</TableHead>
                    <TableHead>{t("bot_target")}</TableHead>
                    <TableHead>{t("bot_message")}</TableHead>
                    <TableHead>{t("bot_status")}</TableHead>
                    <TableHead className="text-right">{t("btn_cancel")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messagesLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                        {t("loading")}
                      </TableCell>
                    </TableRow>
                  ) : !messages || messages.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                        {t("bot_no_scheduled")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    messages.map((item) => {
                      const statusKey = STATUS_LABEL[item.status] ?? "bot_status_pending"
                      const title = item.client?.fullName || item.chatId
                      return (
                        <TableRow key={item.id}>
                          <TableCell className="whitespace-nowrap text-sm">
                            {formatDateTime(item.sendAt)}
                          </TableCell>
                          <TableCell className="max-w-[160px]">
                            <div className="truncate text-sm font-medium">{title}</div>
                            <div className="truncate text-xs text-muted-foreground">
                              {item.chatId}
                              {item.threadId ? ` / ${item.threadId}` : ""}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[260px]">
                            <div className="truncate text-sm" title={item.message}>
                              {item.message}
                            </div>
                            {item.error && (
                              <div className="truncate text-xs text-red-500" title={item.error}>
                                {item.error}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${
                                STATUS_CLASS[item.status] ?? STATUS_CLASS.PENDING
                              }`}
                            >
                              {item.status === "SENT" ? (
                                <CheckCircle2 className="h-3 w-3" />
                              ) : item.status === "FAILED" ? (
                                <XCircle className="h-3 w-3" />
                              ) : null}
                              {t(statusKey)}
                            </span>
                            {item.createdBy && (
                              <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                                {t("bot_created_by")}: {item.createdBy}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.status === "PENDING" ? (
                              <button
                                type="button"
                                onClick={() => cancelMessage.mutate(item.id)}
                                disabled={cancelMessage.isPending}
                                className="h-8 rounded-md border border-input bg-background px-3 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {t("bot_cancel")}
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Bot
